import { useState, useEffect, useRef } from 'react';

interface NewApplicantAnimationState {
  isAnimating: boolean;
  animationPhase: 'flash' | 'fade' | 'complete';
  cardClasses: string;
}

/**
 * Custom hook to manage new applicant animation
 * Triggers animation when a new applicant is detected
 */
export const useNewApplicantAnimation = (
  applicantId: string,
  isNewApplicant: boolean
): NewApplicantAnimationState => {
  const [animationState, setAnimationState] = useState<NewApplicantAnimationState>({
    isAnimating: false,
    animationPhase: 'complete',
    cardClasses: ''
  });

  // Track previous state to detect new applicant
  const previousNewApplicantState = useRef<boolean | undefined>(undefined);
  const timeoutRefs = useRef<NodeJS.Timeout[]>([]);

  // Cleanup function to clear all timeouts
  const clearAllTimeouts = () => {
    timeoutRefs.current.forEach(timeout => clearTimeout(timeout));
    timeoutRefs.current = [];
  };

  useEffect(() => {
    // Trigger animation when isNewApplicant becomes true (new applicant detected)
    const shouldAnimate = 
      previousNewApplicantState.current !== true && 
      isNewApplicant === true;

    if (shouldAnimate) {
      console.log(`Triggering new applicant animation for applicant ${applicantId}`);
      
      // Clear any existing timeouts
      clearAllTimeouts();

      // Start animation sequence
      setAnimationState({
        isAnimating: true,
        animationPhase: 'flash',
        cardClasses: 'animate-new-applicant-flash'
      });

      // Flash phase (500ms)
      const flashTimeout = setTimeout(() => {
        setAnimationState(prev => ({
          ...prev,
          animationPhase: 'fade',
          cardClasses: 'animate-new-applicant-fade'
        }));
      }, 500);
      timeoutRefs.current.push(flashTimeout);

      // Fade phase (1000ms fade)
      const fadeTimeout = setTimeout(() => {
        setAnimationState(prev => ({
          ...prev,
          isAnimating: false,
          animationPhase: 'complete',
          cardClasses: ''
        }));
      }, 500 + 1000);
      timeoutRefs.current.push(fadeTimeout);
    } else if (isNewApplicant !== previousNewApplicantState.current) {
      // Handle other state changes
      setAnimationState(prev => ({
        ...prev,
        isAnimating: false,
        animationPhase: 'complete',
        cardClasses: ''
      }));
    }

    // Update previous state
    previousNewApplicantState.current = isNewApplicant;
  }, [applicantId, isNewApplicant]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearAllTimeouts();
    };
  }, []);

  return animationState;
};

